import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Button, 
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Stack,
  FormControlLabel,
  Checkbox,
  Paper,
  Collapse
} from '@mui/material';
import { FileText, Plus, Search, Eye, Briefcase, Clock, Filter } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { useNavigate, useLocation } from 'react-router-dom';

const statusMap = {
  SUBMITTED: { label: 'Soumis', color: 'info' },
  ANALYSIS: { label: 'En Analyse', color: 'warning' },
  QUOTED: { label: 'Devis Émis', color: 'secondary' },
  ACCEPTED: { label: 'Accepté', color: 'success' },
  REJECTED: { label: 'Refusé', color: 'error' },
  DEVELOPMENT: { label: 'En Développement', color: 'primary' },
  COMPLETED: { label: 'Terminé', color: 'success' },
};

// Statuts qui définissent un projet vs devis
const PROJECT_STATUSES = ['ACCEPTED', 'DEVELOPMENT', 'COMPLETED'];
const DEVIS_STATUSES = ['SUBMITTED', 'ANALYSIS', 'QUOTED', 'REJECTED'];

function SubmissionList({ type = 'devis' }) {
  const [submissions, setSubmissions] = useState([]);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const location = useLocation();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
  const isDev = user?.role === 'DEV' || user?.role === 'TECHNICIEN';
  const isInternalRole = isAdmin || isDev; // Rôles internes voient toutes les soumissions
  const isProjectView = type === 'project';
  
  // Filtres de statut - par défaut, masquer les projets terminés
  const [statusFilters, setStatusFilters] = useState({
    ACCEPTED: true,
    DEVELOPMENT: true,
    COMPLETED: false, // Masqué par défaut
    SUBMITTED: true,
    ANALYSIS: true,
    QUOTED: true,
    REJECTED: false
  });

  const fetchSubmissions = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const url = isInternalRole ? '/api/submissions/admin/all' : '/api/submissions/my';
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) setSubmissions(data);
    } catch (err) {
      console.error('Failed to fetch submissions');
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [location.key]);

  useEffect(() => {
    if (!socket) return;

    const handleRefresh = () => fetchSubmissions();

    socket.on('statusChanged', handleRefresh);
    socket.on('projectCountChanged', handleRefresh);

    return () => {
      socket.off('statusChanged', handleRefresh);
      socket.off('projectCountChanged', handleRefresh);
    };
  }, [socket]);

  // Gérer le toggle des filtres de statut
  const handleStatusFilterToggle = (status) => {
    setStatusFilters(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  // Filter by type (devis vs project), search term, and status filters
  const filteredSubmissions = submissions.filter(sub => {
    const matchesSearch = sub.title.toLowerCase().includes(search.toLowerCase());
    const matchesType = isProjectView 
      ? PROJECT_STATUSES.includes(sub.status)
      : DEVIS_STATUSES.includes(sub.status);
    const matchesStatusFilter = statusFilters[sub.status];
    return matchesSearch && matchesType && matchesStatusFilter;
  });

  // Title based on view type
  const getTitle = () => {
    if (isProjectView) {
      return isInternalRole ? 'Tous les Projets' : 'Mes Projets';
    }
    return isInternalRole ? 'Tous les Devis' : 'Mes Devis';
  };

  const getEmptyMessage = () => {
    if (isProjectView) {
      return 'Aucun projet en cours.';
    }
    return 'Aucun devis trouvé.';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isProjectView ? <Briefcase size={32} color="#00d4aa" /> : <FileText size={32} color="#00d4aa" />}
          {getTitle()}
        </Typography>
        {!isProjectView && !isDev && (
          <Button 
            variant="contained" 
            startIcon={<Plus size={18} />}
            onClick={() => navigate(isInternalRole ? '/admin/submissions/new' : '/portal/submissions/new')}
          >
            Nouvelle demande
          </Button>
        )}
      </Box>

      <Card sx={{ p: 2, mb: 3, backgroundColor: 'background.paper', backgroundImage: 'none' }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            placeholder={isInternalRole ? "Rechercher par titre ou entreprise..." : "Rechercher par titre..."}
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
            }}
          />
          
          {/* Bouton Filtres */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              size="small"
              startIcon={<Filter size={16} />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ textTransform: 'none' }}
            >
              Filtres {!showFilters && `(${Object.values(statusFilters).filter(v => v).length}/${Object.keys(statusFilters).length})`}
            </Button>
            {showFilters && (
              <Button
                size="small"
                onClick={() => setStatusFilters({
                  ACCEPTED: true,
                  DEVELOPMENT: true,
                  COMPLETED: false,
                  SUBMITTED: true,
                  ANALYSIS: true,
                  QUOTED: true,
                  REJECTED: false
                })}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                Réinitialiser
              </Button>
            )}
          </Box>

          {/* Section Filtres dépliable */}
          <Collapse in={showFilters}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                bgcolor: 'rgba(255,255,255,0.02)', 
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 2
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                Filtrer par statut
              </Typography>
              <Stack spacing={1}>
                {isProjectView ? (
                  // Filtres pour les projets
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={statusFilters.ACCEPTED}
                          onChange={() => handleStatusFilterToggle('ACCEPTED')}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="Accepté" color="success" size="small" sx={{ height: 20 }} />
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={statusFilters.DEVELOPMENT}
                          onChange={() => handleStatusFilterToggle('DEVELOPMENT')}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="En Développement" color="primary" size="small" sx={{ height: 20 }} />
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={statusFilters.COMPLETED}
                          onChange={() => handleStatusFilterToggle('COMPLETED')}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="Terminé" color="success" size="small" sx={{ height: 20 }} />
                          <Typography variant="caption" color="text.secondary">(masqué par défaut)</Typography>
                        </Box>
                      }
                    />
                  </>
                ) : (
                  // Filtres pour les devis
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={statusFilters.SUBMITTED}
                          onChange={() => handleStatusFilterToggle('SUBMITTED')}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="Soumis" color="info" size="small" sx={{ height: 20 }} />
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={statusFilters.ANALYSIS}
                          onChange={() => handleStatusFilterToggle('ANALYSIS')}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="En Analyse" color="warning" size="small" sx={{ height: 20 }} />
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={statusFilters.QUOTED}
                          onChange={() => handleStatusFilterToggle('QUOTED')}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="Devis Émis" color="secondary" size="small" sx={{ height: 20 }} />
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={statusFilters.REJECTED}
                          onChange={() => handleStatusFilterToggle('REJECTED')}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="Refusé" color="error" size="small" sx={{ height: 20 }} />
                          <Typography variant="caption" color="text.secondary">(masqué par défaut)</Typography>
                        </Box>
                      }
                    />
                  </>
                )}
              </Stack>
            </Paper>
          </Collapse>
        </Stack>
      </Card>

      {/* Compteur de résultats */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {filteredSubmissions.length} {isProjectView ? 'projet(s)' : 'devis'} trouvé(s)
        </Typography>
      </Box>

      <TableContainer component={Card} sx={{ backgroundColor: 'background.paper', backgroundImage: 'none' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Titre</TableCell>
              {isInternalRole && <TableCell>Entreprise</TableCell>}
              {isInternalRole && <TableCell>Créateur</TableCell>}
              <TableCell>Statut</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSubmissions.map((sub) => (
              <TableRow key={sub.id}>
                <TableCell sx={{ fontWeight: 500 }}>
                  {sub.title}
                  {sub.isUnassigned && (
                    <Chip 
                      icon={<Clock size={12} />}
                      label="Non attribué" 
                      size="small"
                      sx={{ 
                        ml: 1,
                        height: 20,
                        fontSize: '0.7rem',
                        bgcolor: 'rgba(59, 130, 246, 0.15)', 
                        color: '#3b82f6', 
                        borderColor: 'rgba(59, 130, 246, 0.3)',
                        '& .MuiChip-icon': { fontSize: 12 }
                      }}
                      variant="outlined"
                    />
                  )}
                </TableCell>
                {isInternalRole && (
                  <TableCell>
                    {sub.companyName || sub.company?.name || 'N/A'}
                  </TableCell>
                )}
                {isInternalRole && (
                  <TableCell>
                    {sub.createdBy 
                      ? `${sub.createdBy.firstName || ''} ${sub.createdBy.lastName || ''}`.trim() || sub.createdBy.email || 'N/A'
                      : 'N/A'}
                  </TableCell>
                )}
                <TableCell>
                  <Chip 
                    label={statusMap[sub.status]?.label || sub.status} 
                    color={statusMap[sub.status]?.color || 'default'} 
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{new Date(sub.createdAt).toLocaleDateString()}</TableCell>
                <TableCell align="right">
                  <IconButton 
                    color="primary" 
                    size="small" 
                    onClick={() => navigate(`${isInternalRole ? '/admin' : '/portal'}/submissions/${sub.id}`)}
                  >
                    <Eye size={18} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredSubmissions.length === 0 && (
              <TableRow>
                <TableCell colSpan={isInternalRole ? 6 : 4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  {getEmptyMessage()}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default SubmissionList;